import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MutableRefObject, ReactNode } from 'react';
import { Button, Checkbox, FormField, Input, Modal, Select, Spinner, Textarea } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { CreateSkillRequest, FesConfig, Skill, Team, createSkill, deleteSkill, getSkill, isRepositoryManagedSkill, updateSkill } from '../../fes';
import { Session, canEditSkill } from '../../session';
import { EditorSections } from '../EditorSections';
import { SkillsTable } from './SkillsTable';

type FieldPath = Array<string | number>;
type CodeLanguage = 'javascript' | 'json' | 'shell' | 'text' | 'yaml';
type SkillEditorMode = 'create' | 'edit';

const primarySkillFields = new Set(['skillId', 'name', 'description']);
const contentFieldPattern = /(body|content|instruction|markdown|prompt|readme|text)/i;
const skillNamePattern = /^[a-z0-9][a-z0-9-]*$/;
const defaultSkillFileContent = '# New Skill\n\nDescribe when and how this skill should be used.\n';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getPathKey(path: FieldPath): string {
  return JSON.stringify(path);
}

function getLastStringPathItem(path: FieldPath): string | undefined {
  for (let index = path.length - 1; index >= 0; index -= 1) {
    const pathItem = path[index];

    if (typeof pathItem === 'string') {
      return pathItem;
    }
  }

  return undefined;
}

function isBase64ContentPath(path: FieldPath): boolean {
  const lastStringPathItem = getLastStringPathItem(path);

  return Boolean(
    lastStringPathItem && !primarySkillFields.has(lastStringPathItem) && contentFieldPattern.test(lastStringPathItem)
  );
}

function isContentBase64Path(path: FieldPath): boolean {
  return /^content[-_]?base64$/i.test(String(path[path.length - 1] || ''));
}

function normalizeBase64(value: string): string | null {
  const compactValue = value.trim().replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');

  if (!compactValue || !/^[A-Za-z0-9+/]+={0,2}$/.test(compactValue) || compactValue.length % 4 === 1) {
    return null;
  }

  return compactValue.padEnd(Math.ceil(compactValue.length / 4) * 4, '=');
}

function hasUnexpectedControlCharacters(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const characterCode = value.charCodeAt(index);

    if (
      (characterCode >= 0 && characterCode <= 8) ||
      characterCode === 11 ||
      characterCode === 12 ||
      (characterCode >= 14 && characterCode <= 31)
    ) {
      return true;
    }
  }

  return false;
}

function decodeBase64Text(value: string): string | null {
  const normalizedValue = normalizeBase64(value);

  if (!normalizedValue) {
    return null;
  }

  try {
    const binaryValue = window.atob(normalizedValue);
    const escapedValue = Array.from(binaryValue, character =>
      `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    ).join('');
    const decodedValue = decodeURIComponent(escapedValue);

    return hasUnexpectedControlCharacters(decodedValue) ? null : decodedValue;
  } catch {
    return null;
  }
}

function encodeBase64Text(value: string): string {
  const binaryValue = encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_match, hexValue: string) =>
    String.fromCharCode(Number.parseInt(hexValue, 16))
  );

  return window.btoa(binaryValue);
}

function decodeSkillDataForEditor(skill: Skill): { data: Record<string, unknown>; encodedPaths: Set<string> } {
  const encodedPaths = new Set<string>();

  const decodeValue = (value: unknown, path: FieldPath): unknown => {
    if (typeof value === 'string' && isBase64ContentPath(path)) {
      const decodedValue = decodeBase64Text(value);

      if (decodedValue !== null) {
        encodedPaths.add(getPathKey(path));
        return decodedValue;
      }
    }

    if (Array.isArray(value)) {
      return value.map((item, index) => decodeValue(item, [...path, index]));
    }

    if (isRecord(value)) {
      return Object.entries(value).reduce<Record<string, unknown>>(
        (nextValue, [field, fieldValue]) => ({
          ...nextValue,
          [field]: decodeValue(fieldValue, [...path, field]),
        }),
        {}
      );
    }

    return value;
  };

  return {
    data: decodeValue(skill, []) as Record<string, unknown>,
    encodedPaths,
  };
}

function encodeSkillDataForSave(data: Record<string, unknown>, encodedPaths: Set<string>): Record<string, unknown> {
  const encodeValue = (value: unknown, path: FieldPath): unknown => {
    if (typeof value === 'string' && (encodedPaths.has(getPathKey(path)) || isContentBase64Path(path))) {
      return encodeBase64Text(value);
    }

    if (Array.isArray(value)) {
      return value.map((item, index) => encodeValue(item, [...path, index]));
    }

    if (isRecord(value)) {
      return Object.entries(value).reduce<Record<string, unknown>>(
        (nextValue, [field, fieldValue]) => ({
          ...nextValue,
          [field]: encodeValue(fieldValue, [...path, field]),
        }),
        {}
      );
    }

    return value;
  };

  return encodeValue(data, []) as Record<string, unknown>;
}

function getStringValue(data: Record<string, unknown> | null | undefined, field: string): string {
  const value = data?.[field];

  return typeof value === 'string' ? value : '';
}

function getNestedStringValue(data: Record<string, unknown> | null | undefined, parent: string, field: string): string {
  const parentValue = data?.[parent];

  if (!isRecord(parentValue)) {
    return '';
  }

  return getStringValue(parentValue, field);
}

function getValueAtPath(data: unknown, path: FieldPath): unknown {
  return path.reduce<unknown>((currentValue, pathItem) => {
    if (typeof pathItem === 'number') {
      return Array.isArray(currentValue) ? currentValue[pathItem] : undefined;
    }

    return isRecord(currentValue) ? currentValue[pathItem] : undefined;
  }, data);
}

function setValueAtPath(data: Record<string, unknown>, path: FieldPath, nextValue: unknown): Record<string, unknown> {
  const setValue = (currentValue: unknown, currentPath: FieldPath): unknown => {
    if (currentPath.length === 0) {
      return nextValue;
    }

    const [pathItem, ...remainingPath] = currentPath;

    if (typeof pathItem === 'number') {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const nextArray = [...currentArray];
      nextArray[pathItem] = setValue(nextArray[pathItem], remainingPath);

      return nextArray;
    }

    const currentRecord = isRecord(currentValue) ? currentValue : {};

    return {
      ...currentRecord,
      [pathItem]: setValue(currentRecord[pathItem], remainingPath),
    };
  };

  return setValue(data, path) as Record<string, unknown>;
}

function removeArrayItemAtPath(data: Record<string, unknown>, path: FieldPath, itemIndex: number): Record<string, unknown> {
  const currentValue = getValueAtPath(data, path);

  if (!Array.isArray(currentValue)) {
    return data;
  }

  return setValueAtPath(
    data,
    path,
    currentValue.filter((_item, index) => index !== itemIndex)
  );
}

function getDefaultArrayItem(arrayValue: unknown[]): unknown {
  const sampleValue = arrayValue.find(item => item !== null && item !== undefined);

  if (isRecord(sampleValue) && 'type' in sampleValue && 'path' in sampleValue && 'contentBase64' in sampleValue) {
    return {
      type: 'script',
      path: 'scripts/new-file.mjs',
      contentBase64: '',
    };
  }

  if (typeof sampleValue === 'number') {
    return 0;
  }

  if (typeof sampleValue === 'boolean') {
    return false;
  }

  if (isRecord(sampleValue)) {
    return {};
  }

  return '';
}

function getFieldLabel(field: string | number): string {
  if (typeof field === 'number') {
    return `Item ${field + 1}`;
  }

  const words = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

function getContentFieldLabel(field: string): string {
  if (/^content[-_]?base64$/i.test(field)) {
    return 'Content';
  }

  const label = getFieldLabel(field);

  return label === 'Content' ? 'Skill Content' : label;
}

function getEditorFieldLabel(field: string | number): string {
  return typeof field === 'string' && contentFieldPattern.test(field) ? getContentFieldLabel(field) : getFieldLabel(field);
}

function getFieldId(path: FieldPath): string {
  const idSuffix = path
    .map(pathItem => String(pathItem).replace(/[^a-zA-Z0-9_-]/g, '-'))
    .filter(Boolean)
    .join('-');

  return `skill-field-${idSuffix || 'root'}`;
}

function isLongTextField(path: FieldPath, value: unknown): boolean {
  const fieldName = String(path[path.length - 1] || '');

  return typeof value === 'string' && (value.length > 120 || contentFieldPattern.test(fieldName));
}

function isContentField(field: string, value: unknown): value is string {
  return typeof value === 'string' && contentFieldPattern.test(field);
}

function getCodeLanguage(path: FieldPath, parentRecord?: Record<string, unknown>): CodeLanguage {
  const fieldName = String(getLastStringPathItem(path) || '').toLowerCase();
  const recordPath = typeof parentRecord?.path === 'string' ? parentRecord.path : '';
  const recordName = typeof parentRecord?.name === 'string' ? parentRecord.name : '';
  const signal = `${fieldName} ${recordPath} ${recordName}`.toLowerCase();

  if (/(\.ya?ml\b|\byaml\b|\byml\b)/.test(signal)) {
    return 'yaml';
  }

  if (/(\.json\b|\bjson\b)/.test(signal)) {
    return 'json';
  }

  if (/(\.mjs\b|\.cjs\b|\.jsx?\b|\.tsx?\b|\bjavascript\b|\btypescript\b)/.test(signal)) {
    return 'javascript';
  }

  if (/(\.sh\b|\.bash\b|\.zsh\b|\bshell\b|\bbash\b)/.test(signal)) {
    return 'shell';
  }

  return 'text';
}

function highlightInlineTokens(value: string, tokenPattern: RegExp, getClassName: (token: string, tokenIndex: number) => string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let tokenIndex = 0;
  let match = tokenPattern.exec(value);

  while (match) {
    if (match.index > lastIndex) {
      nodes.push(value.slice(lastIndex, match.index));
    }

    nodes.push(
      <span className={getClassName(match[0], tokenIndex)} key={`${match.index}-${tokenIndex}`}>
        {match[0]}
      </span>
    );

    lastIndex = match.index + match[0].length;
    tokenIndex += 1;
    match = tokenPattern.exec(value);
  }

  if (lastIndex < value.length) {
    nodes.push(value.slice(lastIndex));
  }

  return nodes;
}

function highlightYamlValue(value: string): ReactNode[] {
  return highlightInlineTokens(value, /("[^"]*"|'[^']*'|\b(?:true|false|null)\b|-?\b\d+(?:\.\d+)?\b)/g, token => {
    if (/^["']/.test(token)) {
      return 'app-code-token app-code-token--string';
    }

    if (/^(true|false|null)$/.test(token)) {
      return 'app-code-token app-code-token--literal';
    }

    return 'app-code-token app-code-token--number';
  });
}

function highlightYamlLine(line: string, lineIndex: number): ReactNode[] {
  const commentIndex = line.indexOf('#');
  const code = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  const comment = commentIndex >= 0 ? line.slice(commentIndex) : '';
  const keyMatch = code.match(/^(\s*(?:-\s*)?)([^:#\n][^:#]*?)(\s*:\s*)/);
  const nodes: ReactNode[] = [];

  if (keyMatch) {
    nodes.push(keyMatch[1]);
    nodes.push(
      <span className="app-code-token app-code-token--key" key={`yaml-key-${lineIndex}`}>
        {keyMatch[2]}
      </span>
    );
    nodes.push(keyMatch[3]);
    nodes.push(...highlightYamlValue(code.slice(keyMatch[0].length)));
  } else {
    nodes.push(...highlightYamlValue(code));
  }

  if (comment) {
    nodes.push(
      <span className="app-code-token app-code-token--comment" key={`yaml-comment-${lineIndex}`}>
        {comment}
      </span>
    );
  }

  return nodes;
}

function highlightJsonLine(line: string): ReactNode[] {
  return highlightInlineTokens(line, /("(?:\\.|[^"\\])*"|\b(?:true|false|null)\b|-?\b\d+(?:\.\d+)?\b)/g, (token, tokenIndex) => {
    if (/^"/.test(token)) {
      return line.slice(line.indexOf(token) + token.length).trimStart().startsWith(':') && tokenIndex % 2 === 0
        ? 'app-code-token app-code-token--key'
        : 'app-code-token app-code-token--string';
    }

    if (/^(true|false|null)$/.test(token)) {
      return 'app-code-token app-code-token--literal';
    }

    return 'app-code-token app-code-token--number';
  });
}

function highlightShellLine(line: string): ReactNode[] {
  const commentIndex = line.trimStart().startsWith('#') ? line.indexOf('#') : -1;

  if (commentIndex >= 0) {
    return [
      line.slice(0, commentIndex),
      <span className="app-code-token app-code-token--comment" key="shell-comment">
        {line.slice(commentIndex)}
      </span>,
    ];
  }

  return highlightInlineTokens(line, /("[^"]*"|'[^']*'|\b(?:cd|chmod|curl|export|git|if|then|fi|for|do|done|npm|yarn)\b)/g, token =>
    /^["']/.test(token) ? 'app-code-token app-code-token--string' : 'app-code-token app-code-token--keyword'
  );
}

function highlightJavaScriptLine(line: string): ReactNode[] {
  const commentIndex = line.indexOf('//');
  const code = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  const comment = commentIndex >= 0 ? line.slice(commentIndex) : '';
  const nodes = highlightInlineTokens(
    code,
    /(`(?:\\.|[^`\\])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b(?:async|await|break|case|catch|class|const|continue|default|else|export|finally|for|from|function|if|import|let|new|return|switch|throw|try|typeof|while)\b|\b(?:true|false|null|undefined)\b|-?\b\d+(?:\.\d+)?\b)/g,
    token => {
      if (/^[`"']/.test(token)) {
        return 'app-code-token app-code-token--string';
      }

      if (/^(true|false|null|undefined)$/.test(token)) {
        return 'app-code-token app-code-token--literal';
      }

      if (/^-?\d/.test(token)) {
        return 'app-code-token app-code-token--number';
      }

      return 'app-code-token app-code-token--keyword';
    }
  );

  if (comment) {
    nodes.push(
      <span className="app-code-token app-code-token--comment" key="js-comment">
        {comment}
      </span>
    );
  }

  return nodes;
}

function highlightCode(value: string, language: CodeLanguage): ReactNode[] {
  return value.split('\n').map((line, lineIndex, lines) => {
    const lineNodes =
      language === 'yaml'
        ? highlightYamlLine(line, lineIndex)
        : language === 'json'
          ? highlightJsonLine(line)
          : language === 'javascript'
            ? highlightJavaScriptLine(line)
            : language === 'shell'
              ? highlightShellLine(line)
              : [line];

    return (
      <span key={lineIndex}>
        {lineNodes}
        {lineIndex < lines.length - 1 ? '\n' : null}
      </span>
    );
  });
}

function getCodeLanguageLabel(language: CodeLanguage): string {
  return language === 'javascript' ? 'JS' : language.toUpperCase();
}

function CodeTextarea({
  id,
  label,
  language,
  onFullscreenChange,
  onChange,
  readOnly,
  rows,
  value,
}: {
  id: string;
  label: string;
  language: CodeLanguage;
  onFullscreenChange?: (fullscreen: boolean) => void;
  onChange: (value: string) => void;
  readOnly: boolean;
  rows: number;
  value: string;
}) {
  const inlineHighlightRef = useRef<HTMLPreElement | null>(null);
  const fullscreenHighlightRef = useRef<HTMLPreElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const minHeight = Math.max(rows, 8) * 22 + 32;
  const syncHighlightScroll = (textarea: HTMLTextAreaElement, highlight: HTMLPreElement | null) => {
    if (highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
  };
  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    onFullscreenChange?.(false);
  }, [onFullscreenChange]);
  const handleFullscreenEscape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    closeFullscreen();
  };

  useEffect(() => {
    if (!isFullscreen) {
      return undefined;
    }

    onFullscreenChange?.(true);

    const handleWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closeFullscreen();
    };

    window.addEventListener('keydown', handleWindowKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown, true);
      onFullscreenChange?.(false);
    };
  }, [closeFullscreen, isFullscreen, onFullscreenChange]);

  const renderEditorSurface = (highlightRef: MutableRefObject<HTMLPreElement | null>, fullscreen: boolean) => (
    <div className={fullscreen ? 'app-code-editor app-code-editor--fullscreen' : 'app-code-editor'} style={{ minHeight }}>
      <div className="app-code-editor-toolbar">
        <span className="app-code-editor-language">{getCodeLanguageLabel(language)}</span>
        <button
          className="app-code-editor-toolbar-button"
          type="button"
          onClick={() => {
            if (fullscreen) {
              closeFullscreen();
              return;
            }

            setIsFullscreen(true);
          }}
          aria-label={fullscreen ? `Exit ${label} fullscreen editor` : `Open ${label} fullscreen editor`}
        >
          {fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        </button>
      </div>
      <pre className="app-code-editor-highlight" ref={highlightRef} aria-hidden="true">
        <code>{highlightCode(value || ' ', language)}</code>
      </pre>
      <textarea
        id={fullscreen ? `${id}-fullscreen` : id}
        aria-label={fullscreen ? `${label} fullscreen` : undefined}
        className="app-code-editor-input"
        value={value}
        onChange={event => onChange(event.target.value)}
        onKeyDown={fullscreen ? handleFullscreenEscape : undefined}
        onScroll={event => syncHighlightScroll(event.currentTarget, highlightRef.current)}
        readOnly={readOnly}
        spellCheck={false}
        wrap="soft"
      />
    </div>
  );

  return (
    <FormField labelForId={id} labelText={label} type="textarea">
      {renderEditorSurface(inlineHighlightRef, false)}
      {isFullscreen ? (
        <div
          className="app-code-editor-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label={`${label} fullscreen editor`}
          onKeyDown={handleFullscreenEscape}
        >
          <div className="app-code-editor-fullscreen-panel">
            <div className="app-code-editor-fullscreen-header">
              <div>
                <h3 className="app-code-editor-fullscreen-title">{label}</h3>
                <span className="app-code-editor-fullscreen-meta">{getCodeLanguageLabel(language)}</span>
              </div>
              <button
                className="app-code-editor-toolbar-button"
                type="button"
                onClick={closeFullscreen}
                aria-label={`Close ${label} fullscreen editor`}
              >
                Close
              </button>
            </div>
            {renderEditorSurface(fullscreenHighlightRef, true)}
          </div>
        </div>
      ) : null}
    </FormField>
  );
}

function mergeSkillData(baseSkill: Skill | null, data: Record<string, unknown>, skillId: string): Skill {
  const merged = {
    ...(baseSkill || {}),
    ...data,
    skillId,
  };
  const name = typeof merged.name === 'string' && merged.name.trim() ? merged.name : baseSkill?.name || skillId;

  return {
    ...merged,
    skillId,
    name,
  };
}

function createDefaultSkillDraft(): Record<string, unknown> {
  return {
    name: '',
    description: '',
    files: [
      {
        type: 'skill',
        path: 'SKILL.md',
        contentBase64: defaultSkillFileContent,
      },
    ],
  };
}

function getSkillFiles(data: Record<string, unknown>): Array<Record<string, unknown>> {
  const files = data.files;

  return Array.isArray(files)
    ? files.filter((file): file is Record<string, unknown> => Boolean(file) && typeof file === 'object' && !Array.isArray(file))
    : [];
}

function validateCreateSkillRequest(data: Record<string, unknown>): string | null {
  const name = getStringValue(data, 'name').trim();
  const files = getSkillFiles(data);
  const paths = files.map(file => (typeof file.path === 'string' ? file.path.trim() : ''));
  const skillMdCount = paths.filter(path => path === 'SKILL.md').length;
  const duplicatePath = paths.find((path, index) => path && paths.indexOf(path) !== index);

  if (!skillNamePattern.test(name)) {
    return 'Skill name must use lowercase letters, numbers, and hyphens, and start with a letter or number.';
  }

  if (files.length === 0) {
    return 'Skill must include at least one file.';
  }

  if (skillMdCount !== 1) {
    return 'Skill must include exactly one SKILL.md file.';
  }

  if (paths.some(path => !path)) {
    return 'Every skill file needs a path.';
  }

  if (duplicatePath) {
    return `Duplicate file path: ${duplicatePath}`;
  }

  if (files.some(file => getStringValue(file, 'contentBase64').trim().length === 0)) {
    return 'Skill files must not be empty.';
  }

  return null;
}

function buildCreateSkillRequest(data: Record<string, unknown>): CreateSkillRequest {
  return {
    name: getStringValue(data, 'name').trim(),
    ...(getStringValue(data, 'description').trim() ? { description: getStringValue(data, 'description').trim() } : {}),
    files: getSkillFiles(data),
  };
}

function SkillValueField({
  disabled,
  label,
  onAddArrayItem,
  onChange,
  onCodeEditorFullscreenChange,
  onRemoveArrayItem,
  parentRecord,
  path,
  showCollectionActions,
  value,
}: {
  disabled: boolean;
  label: string;
  onAddArrayItem: (path: FieldPath) => void;
  onChange: (path: FieldPath, value: unknown) => void;
  onCodeEditorFullscreenChange: (fullscreen: boolean) => void;
  onRemoveArrayItem: (path: FieldPath, itemIndex: number) => void;
  parentRecord?: Record<string, unknown>;
  path: FieldPath;
  showCollectionActions: boolean;
  value: unknown;
}) {
  const id = getFieldId(path);

  if (Array.isArray(value)) {
    const hasExpandableItems = value.some(item => isRecord(item) || Array.isArray(item));

    return (
      <div className="app-skill-field-group">
        <div className="app-skill-field-group-header">
          <h4 className="app-skill-field-group-title">{label}</h4>
          <span className="app-agent-editor-count">{value.length} items</span>
        </div>
        <div className={hasExpandableItems ? 'app-skill-array-list app-skill-array-list--collapsible' : 'app-skill-array-list'}>
          {value.map((item, index) => {
            const itemPath = [...path, index];
            const itemTitle =
              isRecord(item) && typeof item.path === 'string'
                ? item.path
                : isRecord(item) && typeof item.name === 'string'
                  ? item.name
                  : getFieldLabel(index);
            const itemType = isRecord(item) && typeof item.type === 'string' ? item.type : undefined;

            if (isRecord(item)) {
              return (
                <details className="app-skill-array-detail" key={index} open={index === 0}>
                  <summary className="app-skill-array-summary">
                    <span className="app-skill-array-summary-title">{itemTitle}</span>
                    {itemType ? <span className="app-skill-array-summary-meta">{itemType}</span> : null}
                  </summary>
                  <div className="app-skill-array-detail-body">
                    <div className="app-skill-field-list">
                      {Object.entries(item).map(([field, fieldValue]) => (
                        <SkillValueField
                          disabled={disabled}
                          key={field}
                          label={getEditorFieldLabel(field)}
                          onAddArrayItem={onAddArrayItem}
                          onChange={onChange}
                          onCodeEditorFullscreenChange={onCodeEditorFullscreenChange}
                          onRemoveArrayItem={onRemoveArrayItem}
                          parentRecord={item}
                          path={[...itemPath, field]}
                          showCollectionActions={showCollectionActions}
                          value={fieldValue}
                        />
                      ))}
                    </div>
                    {showCollectionActions ? (
                      <div className="app-skill-array-detail-actions">
                        <Button secondary onClick={() => onRemoveArrayItem(path, index)} disabled={disabled}>
                          Remove
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </details>
              );
            }

            if (Array.isArray(item)) {
              return (
                <details className="app-skill-array-detail" key={index}>
                  <summary className="app-skill-array-summary">
                    <span className="app-skill-array-summary-title">{getFieldLabel(index)}</span>
                    <span className="app-skill-array-summary-meta">{item.length} items</span>
                  </summary>
                  <div className="app-skill-array-detail-body">
                    <SkillValueField
                      disabled={disabled}
                      label={getFieldLabel(index)}
                      onAddArrayItem={onAddArrayItem}
                      onChange={onChange}
                      onCodeEditorFullscreenChange={onCodeEditorFullscreenChange}
                      onRemoveArrayItem={onRemoveArrayItem}
                      path={itemPath}
                      showCollectionActions={showCollectionActions}
                      value={item}
                    />
                    {showCollectionActions ? (
                      <div className="app-skill-array-detail-actions">
                        <Button secondary onClick={() => onRemoveArrayItem(path, index)} disabled={disabled}>
                          Remove
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </details>
              );
            }

            return (
              <div className="app-skill-array-item" key={index}>
                <SkillValueField
                  disabled={disabled}
                  label={getFieldLabel(index)}
                  onAddArrayItem={onAddArrayItem}
                  onChange={onChange}
                  onCodeEditorFullscreenChange={onCodeEditorFullscreenChange}
                  onRemoveArrayItem={onRemoveArrayItem}
                  path={itemPath}
                  showCollectionActions={showCollectionActions}
                  value={item}
                />
                {showCollectionActions ? (
                  <Button secondary onClick={() => onRemoveArrayItem(path, index)} disabled={disabled}>
                    Remove
                  </Button>
                ) : null}
              </div>
            );
          })}
          {value.length === 0 ? <p className="app-empty">No items found.</p> : null}
        </div>
        {showCollectionActions ? (
          <div>
            <Button secondary onClick={() => onAddArrayItem(path)} disabled={disabled}>
              Add item
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (isRecord(value)) {
    const entries = Object.entries(value);

    return (
      <div className="app-skill-field-group">
        <h4 className="app-skill-field-group-title">{label}</h4>
        <div className="app-skill-field-list">
          {entries.map(([field, fieldValue]) => (
            <SkillValueField
              disabled={disabled}
              key={field}
              label={getEditorFieldLabel(field)}
              onAddArrayItem={onAddArrayItem}
              onChange={onChange}
              onCodeEditorFullscreenChange={onCodeEditorFullscreenChange}
              onRemoveArrayItem={onRemoveArrayItem}
              parentRecord={value}
              path={[...path, field]}
              showCollectionActions={showCollectionActions}
              value={fieldValue}
            />
          ))}
          {entries.length === 0 ? <p className="app-empty">No fields found.</p> : null}
        </div>
      </div>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <Checkbox id={id} checked={value} disabled={disabled} onChange={event => onChange(path, event.target.checked)}>
        {label}
      </Checkbox>
    );
  }

  if (typeof value === 'number') {
    return (
      <FormField labelForId={id} labelText={label} type="input">
        <Input
          id={id}
          type="number"
          value={String(value)}
          onChange={event => {
            const nextValue = event.target.value.trim();
            const parsedValue = Number(nextValue);

            onChange(path, nextValue && Number.isFinite(parsedValue) ? parsedValue : undefined);
          }}
          readOnly={disabled}
        />
      </FormField>
    );
  }

  const textValue = typeof value === 'string' ? value : '';

  if (isLongTextField(path, value)) {
    return (
      <CodeTextarea
        id={id}
        label={label}
        language={getCodeLanguage(path, parentRecord)}
        onFullscreenChange={onCodeEditorFullscreenChange}
        onChange={nextValue => onChange(path, nextValue)}
        readOnly={disabled}
        rows={12}
        value={textValue}
      />
    );
  }

  return (
    <FormField labelForId={id} labelText={label} type="input">
      <Input id={id} value={textValue} onChange={event => onChange(path, event.target.value)} readOnly={disabled} />
    </FormField>
  );
}

export function SkillsView({
  creatingSkill,
  editingSkillId,
  fesConfig,
  onSkillDeleted,
  onCreateSkillClose,
  onCreateSkillOpen,
  onEditSkillClose,
  onEditSkillOpen,
  onSkillSaved,
  session,
  skills,
  teams,
  tokens,
}: {
  creatingSkill?: boolean;
  editingSkillId?: string;
  fesConfig: FesConfig;
  session: Session;
  teams: Team[];
  onSkillDeleted: (skillId: string) => void;
  onCreateSkillClose?: () => void;
  onCreateSkillOpen?: () => void;
  onEditSkillClose?: () => void;
  onEditSkillOpen?: (skill: Skill) => void;
  onSkillSaved: (skill: Skill) => void;
  skills: Skill[];
  tokens: AuthTokens;
}) {
  const latestSkillRequest = useRef(0);
  const encodedSkillFieldPaths = useRef<Set<string>>(new Set());
  const [createTeamId, setCreateTeamId] = useState(session.activeTeamId ?? '');
  const [skillEditorMode, setSkillEditorMode] = useState<SkillEditorMode>('edit');
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [skillDraft, setSkillDraft] = useState<Record<string, unknown> | null>(null);
  const [isLoadingSkill, setIsLoadingSkill] = useState(false);
  const [hasLoadedSkill, setHasLoadedSkill] = useState(false);
  const [isSavingSkill, setIsSavingSkill] = useState(false);
  const [isCodeEditorFullscreen, setIsCodeEditorFullscreen] = useState(false);
  const [editError, setEditError] = useState<string>();
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);
  const [isDeletingSkill, setIsDeletingSkill] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const [listSearch, setListSearch] = useState('');

  const handleEditOpen = useCallback((skill: Skill) => {
    if (!skill.skillId) {
      return;
    }

    const requestId = latestSkillRequest.current + 1;
    latestSkillRequest.current = requestId;
    const decodedSkill = decodeSkillDataForEditor(skill);
    setSkillEditorMode('edit');
    encodedSkillFieldPaths.current = decodedSkill.encodedPaths;
    setEditingSkill(skill);
    setSkillDraft(decodedSkill.data);
    setIsLoadingSkill(true);
    setHasLoadedSkill(false);
    setIsSavingSkill(false);
    setIsCodeEditorFullscreen(false);
    setEditError(undefined);

    const load = async () => {
      try {
        const skillResponse = await getSkill(fesConfig, tokens, skill.skillId);

        if (latestSkillRequest.current !== requestId) {
          return;
        }

        const fullSkill = skillResponse.data
          ? { ...skillResponse.data, managedBy: skillResponse.data.managedBy ?? skill.managedBy }
          : skill;
        const decodedFullSkill = decodeSkillDataForEditor(fullSkill);
        encodedSkillFieldPaths.current = decodedFullSkill.encodedPaths;
        setEditingSkill(fullSkill);
        setSkillDraft(decodedFullSkill.data);
        setHasLoadedSkill(true);
      } catch (error) {
        if (latestSkillRequest.current !== requestId) {
          return;
        }

        setEditError(error instanceof Error ? error.message : 'Unable to load full skill.');
      } finally {
        if (latestSkillRequest.current === requestId) {
          setIsLoadingSkill(false);
        }
      }
    };

    void load();
  }, [fesConfig, tokens]);

  const handleCreateOpen = useCallback(() => {
    latestSkillRequest.current += 1;
    setSkillEditorMode('create');
    encodedSkillFieldPaths.current = new Set([getPathKey(['files', 0, 'contentBase64'])]);
    setEditingSkill({ skillId: '', name: '', files: [], createdAt: '', updatedAt: '', createdBy: '' });
    setSkillDraft(createDefaultSkillDraft());
    setIsLoadingSkill(false);
    setHasLoadedSkill(true);
    setIsSavingSkill(false);
    setIsCodeEditorFullscreen(false);
    setEditError(undefined);
  }, []);

  const handleEditClose = useCallback((notifyRoute = true) => {
    if (isSavingSkill || isCodeEditorFullscreen) {
      return;
    }

    latestSkillRequest.current += 1;
    setEditingSkill(null);
    setSkillDraft(null);
    setSkillEditorMode('edit');
    encodedSkillFieldPaths.current = new Set();
    setIsLoadingSkill(false);
    setHasLoadedSkill(false);
    setIsCodeEditorFullscreen(false);
    setEditError(undefined);
    if (notifyRoute) {
      if (skillEditorMode === 'create') {
        onCreateSkillClose?.();
      } else {
        onEditSkillClose?.();
      }
    }
  }, [isCodeEditorFullscreen, isSavingSkill, onCreateSkillClose, onEditSkillClose, skillEditorMode]);

  useEffect(() => {
    if (creatingSkill) {
      if (skillEditorMode !== 'create' || !editingSkill) {
        handleCreateOpen();
      }

      return;
    }

    if (skillEditorMode === 'create') {
      handleEditClose(false);
      return;
    }

    if (!editingSkillId) {
      if (editingSkill) {
        handleEditClose(false);
      }

      return;
    }

    if (editingSkill?.skillId === editingSkillId) {
      return;
    }

    const skillFromList = skills.find(skill => skill.skillId === editingSkillId);

    handleEditOpen(skillFromList || { skillId: editingSkillId, name: editingSkillId });
  }, [creatingSkill, editingSkill, editingSkillId, handleCreateOpen, handleEditClose, handleEditOpen, skillEditorMode, skills]);

  const updateSkillDraft = (updater: (data: Record<string, unknown>) => Record<string, unknown>) => {
    setSkillDraft(currentDraft => (currentDraft ? updater(currentDraft) : currentDraft));
    setEditError(undefined);
  };

  const updateSkillDraftField = (field: string, value: string) => {
    updateSkillDraft(data => ({
      ...data,
      [field]: value,
    }));
  };

  const updateSkillDraftPath = (path: FieldPath, value: unknown) => {
    updateSkillDraft(data => setValueAtPath(data, path, value));
  };

  const handleAddArrayItem = (path: FieldPath) => {
    if (path.length === 1 && path[0] === 'files') {
      const files = getSkillFiles(skillDraft || {});
      encodedSkillFieldPaths.current.add(getPathKey(['files', files.length, 'contentBase64']));
    }

    updateSkillDraft(data => {
      const currentValue = getValueAtPath(data, path);
      const arrayValue = Array.isArray(currentValue) ? currentValue : [];

      return setValueAtPath(data, path, [...arrayValue, getDefaultArrayItem(arrayValue)]);
    });
  };

  const handleRemoveArrayItem = (path: FieldPath, itemIndex: number) => {
    updateSkillDraft(data => removeArrayItemAtPath(data, path, itemIndex));
  };

  const handleSkillSave = () => {
    if (
      !skillDraft ||
      (skillEditorMode === 'edit' && editingSkill && !canEditSkill(editingSkill, session))
    ) {
      return;
    }

    const skillData = encodeSkillDataForSave(skillDraft, encodedSkillFieldPaths.current);
    setIsSavingSkill(true);
    setEditError(undefined);

    const save = async () => {
      try {
        if (skillEditorMode === 'create') {
          const validationError = validateCreateSkillRequest(skillDraft);

          if (validationError) {
            setEditError(validationError);
            setIsSavingSkill(false);
            return;
          }

          const createRequest = buildCreateSkillRequest(skillData);
          const skillResponse = await createSkill(fesConfig, tokens, createRequest, {
            teamId: createTeamId || undefined,
          });
          const savedSkill = skillResponse.data || mergeSkillData(editingSkill, createRequest, createRequest.name);

          onSkillSaved(savedSkill);
          handleEditClose();
          return;
        }

        const skillId = editingSkill?.skillId;

        if (!skillId) {
          return;
        }

        const skillResponse = await updateSkill(fesConfig, tokens, skillId, skillData);
        const savedSkill = skillResponse.data || mergeSkillData(editingSkill, skillData, skillId);

        onSkillSaved(savedSkill);
        handleEditClose();
      } catch (error) {
        setEditError(error instanceof Error ? error.message : 'Unable to save skill.');
      } finally {
        setIsSavingSkill(false);
      }
    };

    void save();
  };

  const handleDeleteClose = () => {
    if (isDeletingSkill) {
      return;
    }

    setSkillToDelete(null);
    setDeleteError(undefined);
  };

  const handleSkillDelete = () => {
    const skillId = skillToDelete?.skillId;

    if (!skillId) {
      return;
    }

    setIsDeletingSkill(true);
    setDeleteError(undefined);

    const remove = async () => {
      try {
        await deleteSkill(fesConfig, tokens, skillId);
        onSkillDeleted(skillId);
        setSkillToDelete(null);
      } catch (error) {
        setDeleteError(error instanceof Error ? error.message : 'Unable to delete skill.');
      } finally {
        setIsDeletingSkill(false);
      }
    };

    void remove();
  };

  const isCreatingSkill = skillEditorMode === 'create';
  const skillName = getStringValue(skillDraft, 'name');
  const isViewingRepositoryManagedSkill = Boolean(
    !isCreatingSkill && editingSkill && isRepositoryManagedSkill(editingSkill)
  );
  const isSkillDraftReadOnly = Boolean(
    !isCreatingSkill && editingSkill && !canEditSkill(editingSkill, session)
  );
  const isSkillDraftEditable = Boolean(
    skillDraft &&
      hasLoadedSkill &&
      !isLoadingSkill &&
      !isSavingSkill &&
      !isSkillDraftReadOnly
  );
  const teamNames = new Map(teams.map(team => [team.teamId, team.name || team.teamId]));
  const canSaveSkill = Boolean(
    hasLoadedSkill &&
      !isLoadingSkill &&
      !isSavingSkill &&
      !isSkillDraftReadOnly &&
      skillDraft &&
      skillName.trim() &&
      (isCreatingSkill || editingSkill?.skillId)
  );
  const skillDescription = getStringValue(skillDraft, 'description');
  const skillId = getStringValue(skillDraft, 'skillId') || editingSkill?.skillId || '-';
  const userId = getStringValue(skillDraft, 'userId') || '-';
  const updatedAt = getNestedStringValue(skillDraft, 'metadata', 'updatedAt') || getStringValue(skillDraft, 'updatedAt') || '-';
  const contentFieldEntries = Object.entries(skillDraft || {}).filter(
    ([field, value]) => !primarySkillFields.has(field) && isContentField(field, value)
  );
  const contentFieldNames = new Set(contentFieldEntries.map(([field]) => field));
  const additionalFieldEntries = Object.entries(skillDraft || {}).filter(
    ([field]) => !primarySkillFields.has(field) && !contentFieldNames.has(field)
  );
  const normalizedListSearch = listSearch.trim().toLowerCase();
  const filteredSkills = normalizedListSearch
    ? skills.filter(skill => {
        const teamName = skill.teamId ? teamNames.get(skill.teamId) : 'All teams';
        const source = isRepositoryManagedSkill(skill) ? 'skills-library' : 'Backoffice';

        return [skill.name, skill.description, skill.skillId, teamName, source]
          .some(value => value?.toLowerCase().includes(normalizedListSearch));
      })
    : skills;

  return (
    <>
      <div className="app-table-toolbar">
        <Input
          aria-label="Search skills"
          className="app-table-search"
          onChange={event => setListSearch(event.target.value)}
          placeholder="Search skills"
          type="search"
          value={listSearch}
        />
        <Button icon="semantic-plus" onClick={() => (onCreateSkillOpen ? onCreateSkillOpen() : handleCreateOpen())}>
          New Skill
        </Button>
      </div>
      <SkillsTable
        canEdit={skill => canEditSkill(skill, session)}
        skills={filteredSkills}
        teamNames={teamNames}
        onDeleteSkill={skill => {
          setSkillToDelete(skill);
          setDeleteError(undefined);
        }}
        onEditSkill={skill => (onEditSkillOpen ? onEditSkillOpen(skill) : handleEditOpen(skill))}
      />
      {editingSkill ? (
        <Modal
          buttonCancelLabel={
            isCreatingSkill
              ? 'Cancel create skill'
              : isViewingRepositoryManagedSkill
                ? 'Close skill view'
                : 'Cancel edit skill'
          }
          buttonCancelText={isViewingRepositoryManagedSkill ? 'Close' : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={
            isViewingRepositoryManagedSkill
              ? undefined
              : isCreatingSkill
                ? 'Create skill'
                : `Save skill ${editingSkill.name || editingSkill.skillId}`
          }
          buttonConfirmText={
            isViewingRepositoryManagedSkill
              ? null
              : isSavingSkill
                ? isCreatingSkill
                  ? 'Creating...'
                  : 'Saving...'
                : isCreatingSkill
                  ? 'Create'
                  : 'Save'
          }
          confirmationDisabled={!canSaveSkill}
          onCancel={() => handleEditClose()}
          onClose={() => handleEditClose()}
          onConfirm={handleSkillSave}
          shown
          large
          left
          stretch
          className="app-skill-editor-modal"
          status={editError ? 'error' : undefined}
          title={isCreatingSkill ? 'Create skill' : isViewingRepositoryManagedSkill ? 'View skill' : 'Edit skill'}
        >
          <>
            <div className="app-skill-editor">
              {!isCreatingSkill ? (
                <div className="app-agent-editor-summary" aria-label="Skill identity">
                  <div className="app-agent-editor-summary-item">
                    <span>Skill ID</span>
                    <strong>{skillId}</strong>
                  </div>
                  <div className="app-agent-editor-summary-item">
                    <span>User ID</span>
                    <strong>{userId}</strong>
                  </div>
                  <div className="app-agent-editor-summary-item">
                    <span>Updated At</span>
                    <strong>{updatedAt}</strong>
                  </div>
                </div>
              ) : null}

              {isViewingRepositoryManagedSkill ? (
                <div className="app-result app-skill-readonly-note" role="status">
                  Read-only: this skill is managed in the <strong>skills-library</strong>{' '}
                  repository. To change it, open a pull request there; the update is published
                  automatically once merged.
                </div>
              ) : null}

              {isLoadingSkill ? (
                <div className="app-detail-loading app-detail-loading--compact" role="status">
                  <Spinner center>Loading skill...</Spinner>
                </div>
              ) : null}
              {editError ? (
                <div className="app-result app-team-error" role="alert">
                  {editError}
                </div>
              ) : null}

              <div className="app-skill-editor-main">
                <EditorSections
                  sections={[
                    {
                      id: 'general',
                      content: (
                  <section className="app-agent-editor-section" aria-labelledby="skill-general-fields-heading">
                    <h3 id="skill-general-fields-heading" className="app-agent-editor-section-title">
                      General
                    </h3>
                    <div className="app-skill-editor-grid">
                      <FormField labelForId="skill-name" labelText="Name" notion type="input">
                        <Input
                          id="skill-name"
                          value={skillName}
                          onChange={event => updateSkillDraftField('name', event.target.value)}
                          readOnly={!isSkillDraftEditable}
                        />
                      </FormField>
                      {isCreatingSkill ? (
                        <FormField
                          labelForId="skill-team-select"
                          labelText="Available to"
                          type="select"
                          hint={
                            createTeamId
                              ? 'Only this team can see and attach the skill.'
                              : 'Shared with every team, like the skills published from skills-library.'
                          }
                        >
                          <Select
                            id="skill-team-select"
                            value={createTeamId}
                            onChange={event => setCreateTeamId(event.target.value)}
                            disabled={!isSkillDraftEditable}
                          >
                            {session.isAdmin ? <Select.Item value="">All teams</Select.Item> : null}
                            {teams.map(team => (
                              <Select.Item key={team.teamId} value={team.teamId}>
                                {team.name || team.teamId}
                              </Select.Item>
                            ))}
                          </Select>
                        </FormField>
                      ) : null}
                      <FormField labelForId="skill-description" labelText="Description" type="textarea">
                        <Textarea
                          id="skill-description"
                          value={skillDescription}
                          onChange={event => updateSkillDraftField('description', event.target.value)}
                          readOnly={!isSkillDraftEditable}
                          rows={5}
                        />
                      </FormField>
                    </div>
                  </section>
                      ),
                    },

                    ...(contentFieldEntries.length > 0 ? [{
                      id: 'content',
                      content: (
                      <section className="app-agent-editor-section" aria-labelledby="skill-content-fields-heading">
                      <h3 id="skill-content-fields-heading" className="app-agent-editor-section-title">
                        Content
                      </h3>
                      <div className="app-skill-editor-grid">
                        {contentFieldEntries.map(([field, value]) => (
                          <CodeTextarea
                            key={field}
                            id={`skill-content-${field}`}
                            label={getContentFieldLabel(field)}
                            language={getCodeLanguage([field], skillDraft || undefined)}
                            onFullscreenChange={setIsCodeEditorFullscreen}
                            onChange={nextValue => updateSkillDraftField(field, nextValue)}
                            readOnly={!isSkillDraftEditable}
                            rows={14}
                            value={String(value)}
                          />
                        ))}
                      </div>
                      </section>
                      ),
                    }] : []),

                    {
                      id: 'advanced',
                      content: (
                <section className="app-agent-editor-section" aria-labelledby="skill-additional-fields-heading">
                  <div className="app-agent-editor-section-header">
                    <h3 id="skill-additional-fields-heading" className="app-agent-editor-section-title">
                      Fields
                    </h3>
                    <span className="app-agent-editor-count">{additionalFieldEntries.length} fields</span>
                  </div>
                  <div className="app-skill-field-list">
                    {additionalFieldEntries.map(([field, value]) => (
                      <SkillValueField
                        disabled={!isSkillDraftEditable}
                        key={field}
                        label={getEditorFieldLabel(field)}
                        onAddArrayItem={handleAddArrayItem}
                        onChange={updateSkillDraftPath}
                        onCodeEditorFullscreenChange={setIsCodeEditorFullscreen}
                        onRemoveArrayItem={handleRemoveArrayItem}
                        parentRecord={skillDraft || undefined}
                        path={[field]}
                        showCollectionActions={!isViewingRepositoryManagedSkill}
                        value={value}
                      />
                    ))}
                    {additionalFieldEntries.length === 0 ? <p className="app-empty">No additional fields found.</p> : null}
                  </div>
                </section>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </>
        </Modal>
      ) : null}
      {skillToDelete ? (
        <Modal
          buttonCancelLabel="Cancel delete skill"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Delete skill ${skillToDelete.name || skillToDelete.skillId}`}
          buttonConfirmText={isDeletingSkill ? 'Deleting...' : 'Delete'}
          confirmationDisabled={isDeletingSkill}
          onCancel={handleDeleteClose}
          onClose={handleDeleteClose}
          onConfirm={handleSkillDelete}
          shown
          status="warning"
          title="Delete skill"
        >
          <>
            {deleteError ? (
              <div className="app-result app-team-error" role="alert">
                {deleteError}
              </div>
            ) : null}
            <p className="app-modal-copy">
              Delete skill <strong>{skillToDelete.name || skillToDelete.skillId}</strong>? This cannot be undone.
            </p>
          </>
        </Modal>
      ) : null}
    </>
  );
}
